function copyNode(from, to) {
	to.innerHTML = from.innerHTML;

	for (const attribute of from.attributes) {
		to.setAttribute(attribute.name, attribute.value);
	}
}

function resetInput(elem) {
	if (elem.type) {
		switch (elem.type) {
			case "radio":
			case "checkbox":
				elem.removeAttribute("checked");
				break;
			case "hidden":
				// no-op
				break;
			default:
				elem.value = "";
				break;
		}
	}
}

function getDelegateTarget(e, selector, root = document.documentElement) {
	let node = e.target;

	while (node && node !== root) {
		if (node.matches(selector)) {
			return node;
		}

		node = node.parentNode;
	}

	return null;
}

export class AdvancedQuery {
	constructor(node, opts = {}) {
		this.node = node;
		this.opts = {
			ajax: true,
			facets: ".advanced-query-facets",
			facetsFacet: ".advanced-query-facet",
			facetOpened: "advanced-query-facet--opened",
			facetsToggle: ".advanced-query-facet__toggle",
			facetFilters: ".advanced-query-facet__filters",
			facetsToggler: ".advanced-query-facets__toggle",
			facetsOpened: "advanced-query-facets--opened",
			active: ".advanced-query-active",
			activeFilter: ".advanced-query-active__filter",
			results: ".advanced-query-results",
			pagination: ".advanced-query-pagination",
			paginationLink: ".advanced-query-pagination a",
			paginationMore: ".advanced-query-pagination__more",
			summary: ".advanced-query-summary",
			...opts,
		};

		this.querySelectors();
		this.opened = this.facets
			? this.facets.classList.contains(this.opts.openedClass)
			: false;
		this.wasFocused = null;
		this.page = 1;

		// handle the search submission
		this.node.addEventListener("submit", (e) => {
			e.preventDefault();
			this.fetch(this.url());
		});

		// handle toggling of a facet dropdown
		this.node.addEventListener("click", (e) => {
			const target = getDelegateTarget(
				e,
				this.opts.facetsToggle,
				this.node
			);

			if (target) {
				const facet = target.closest(this.opts.facetsFacet);

				if (facet) {
					facet.classList.toggle(this.opts.facetOpened);

					if (this.getFacetMode() === "dropdown") {
						const filters = facet.querySelector(
							this.opts.facetFilters
						);

						if (filters) {
							const onTransitionEnd = () => {
								const filtersBox =
									filters.getBoundingClientRect();

								// make sure the dropdown doesn't go lower than the viewport
								if (
									filtersBox.bottom >
									document.documentElement.clientHeight
								) {
									filters.style.maxHeight = `${
										filtersBox.height -
										(filtersBox.bottom -
											document.documentElement
												.clientHeight) -
										5
									}px`;
								}

								filters.removeEventListener(
									"transitionend",
									onTransitionEnd
								);
							};

							filters.addEventListener(
								"transitionend",
								onTransitionEnd
							);
							setTimeout(onTransitionEnd, 500);
						}
					}
				}
			}
		});

		// handle "blur" of a facet dropdown by checking outer click (capturing)
		document.body.addEventListener("click", (e) => {
			// only close the other facets if our facet isn't full width (horizontal above a media query)
			if (this.getFacetMode() === "dropdown") {
				let node = e.target;

				while (node && node.parentNode) {
					if (node.matches(this.opts.facetsFacet)) {
						document
							.querySelectorAll(this.opts.facetsFacet)
							.forEach((n) => {
								if (n !== node) {
									n.classList.remove(this.opts.facetOpened);
								}
							});
						return;
					}

					node = node.parentNode;
				}

				document
					.querySelectorAll(this.opts.facetsFacet)
					.forEach((n) => n.classList.remove(this.opts.facetOpened));
			}
		});

		// handle toggling the facets on mobile
		this.node.addEventListener("click", (e) => {
			const node = getDelegateTarget(
				e,
				this.opts.facetsToggler,
				this.node
			);

			if (node) {
				e.preventDefault();
				this.toggleFacets();
			}
		});

		// handle toggling of a filter
		this.node.addEventListener("change", ({ target }) => {
			if (["checkbox", "radio", "select-one"].includes(target.type)) {
				this.page = 1;

				// Check if this is a button-group facet (has the button-group class on parent)
				const isButtonGroup = target.closest(
					".advanced-query-facet--button-group"
				);

				// If changing a select dropdown OR button-group checkbox
				if (target.type === "select-one" || isButtonGroup) {
					// If "All" option selected (empty value), only clear THIS dropdown's filter
					// Other filters (search, other taxonomies) remain unchanged
					if (target.type === "select-one" && target.value === "") {
						// Don't clear other filters - just this dropdown resets to "All"
						// The form submission will exclude this empty value
					} else if (isButtonGroup && target.checked) {
						// Button-group supports multi-select - don't clear other checkboxes
						// This allows additive filtering where counts match behavior
					}
					// For non-empty select values: do nothing special
					// The selected value will be added to the query alongside other active filters
					// This makes filters ADDITIVE rather than exclusive
				}

				this.wasFocused =
					target.type === "select-one"
						? `[name="${target.name}"]`
						: `[name="${target.name}"][value="${target.value}"]`;
				this.fetch(this.url());
			}
		});

		// handle clearing a filter
		this.node.addEventListener("click", (e) => {
			const node = getDelegateTarget(
				e,
				this.opts.activeFilter,
				this.node
			);

			if (node && node.dataset.name && node.dataset.value) {
				const input = this.node.querySelector(
					`[name="${node.dataset.name}"][value="${node.dataset.value}"]`
				);

				if (input) {
					this.page = 1;
					resetInput(input);
					this.fetch(this.url());
				}
			}
		});

		// handle clearing all filters
		this.node.addEventListener("reset", (e) => {
			for (const elem of this.node.elements) {
				resetInput(elem);
			}

			this.page = 1;
			this.fetch(this.url());
		});

		// handle pagination
		this.node.addEventListener("click", (e) => {
			const node = getDelegateTarget(
				e,
				this.opts.paginationLink,
				this.node
			);

			if (node) {
				e.preventDefault();
				this.fetch(e.target.href);
			}
		});

		// handle load more
		this.node.addEventListener("click", (e) => {
			const node = getDelegateTarget(
				e,
				this.opts.paginationMore,
				this.node
			);

			if (node) {
				e.preventDefault();
				this.page++;
				this.fetch(this.url(), true);
			}
		});

		if (this.opts.ajax) {
			// handle back button
			window.addEventListener("popstate", () =>
				this.fetch(location.href, false)
			);
		}
	}

	querySelectors() {
		this.results = this.node.querySelector(this.opts.results);
		this.resultsSet = this.node.querySelector(this.opts.resultsSet);
		this.pagination = this.node.querySelector(this.opts.pagination);
		this.facets = this.node.querySelector(this.opts.facets);
		this.facetsFacets = this.facets.querySelectorAll(this.opts.facetsFacet);
		this.active = this.node.querySelector(this.opts.active);
		this.opener = this.node.querySelector(this.opts.opener);
		this.summary = this.node.querySelector(this.opts.summary);
	}

	url() {
		const formData = new FormData(this.node);
		const formSearchParams = new URLSearchParams(formData);
		const urlSearchParams = new URLSearchParams(location.search);

		// remove any fields from the url params that overlap with fields on the form
		Array.from(this.node.elements)
			.map((el) => el.name)
			.filter((v) => v)
			.forEach((name) => {
				urlSearchParams.delete(name);
			});

		// add form params to the url params (skip empty values)
		for (const param of formSearchParams.keys()) {
			const values = formSearchParams.getAll(param);

			for (const value of values) {
				// Only add non-empty values
				if (value && !urlSearchParams.has(param, value)) {
					urlSearchParams.append(param, value);
				}
			}
		}

		if (this.page > 1) {
			urlSearchParams.set("filter[paged]", this.page);
		} else {
			urlSearchParams.delete("filter[paged]");
		}

		// Remove empty values (create array first to avoid iterator issues)
		const entriesToRemove = [];
		for (const [param, value] of urlSearchParams.entries()) {
			if (!value) {
				entriesToRemove.push(param);
			}
		}
		entriesToRemove.forEach((param) => urlSearchParams.delete(param));

		const hasQueryString = Array.from(urlSearchParams.keys()).length > 0;

		return `${this.node.action}${
			hasQueryString ? "?" : ""
		}${urlSearchParams}`;
	}

	async fetch(url, append = false) {
		if (!this.opts.ajax) {
			window.location.href = url;
			return;
		}

		this.node.classList.add("is-loading");
		this.node
			.querySelectorAll("fieldset")
			.forEach((facet) => (facet.disabled = true));

		try {
			const response = await fetch(url);

			if (!response.ok) {
				throw response;
			}

			const html = await response.text();

			// @see - https://developer.mozilla.org/en-US/docs/Web/API/DOMParser
			const parser = new DOMParser();
			const doc = parser.parseFromString(html, "text/html");

			const facets = doc.querySelector(this.opts.facets);
			const results = doc.querySelector(this.opts.results);
			const pagination = doc.querySelector(this.opts.pagination);
			const active = doc.querySelector(this.opts.active);
			const summary = doc.querySelector(this.opts.summary);

			if (append) {
				if (this.results) {
					this.results.insertAdjacentHTML(
						"beforeend",
						results.innerHTML
					);
					// Dispatch event after new cards are added
					document.dispatchEvent(
						new Event("resources_cards_updated")
					);
				}
			} else {
				if (facets) {
					// Update ALL facets containers, not just the first one
					// This handles cases where multiple .advanced-query-facets exist
					const allFacetsContainers = this.node.querySelectorAll(
						this.opts.facets
					);
					const allNewFacets = doc.querySelectorAll(this.opts.facets);

					allFacetsContainers.forEach((container, index) => {
						if (allNewFacets[index]) {
							copyNode(allNewFacets[index], container);
						}
					});
				}

				if (results) {
					copyNode(results, this.results);
					// Dispatch event after new cards are added
					document.dispatchEvent(
						new Event("resources_cards_updated")
					);
				}

				if (active) {
					copyNode(active, this.active);
				}

				if (summary) {
					copyNode(summary, this.summary);
				}
			}

			if (this.pagination) {
				if (pagination) {
					this.pagination.style.display = "";
					copyNode(pagination, this.pagination);
				} else {
					this.pagination.style.display = "none";
				}
			}

			this.node.dispatchEvent(
				new CustomEvent("advanced-query:reload", {
					bubbles: true,
					cancelable: false,
				})
			);

			if (!append) {
				window.history.pushState({}, "", url);
			}
		} catch (fault) {
			console.error(fault);

			if (fault instanceof Response && fault.status === 404) {
				this.results.innerHTML =
					"No results found for your filters. Please try again.";
				this.pagination.innerHTML = "";

				return;
			}

			window.location.href = url;
		} finally {
			this.node.classList.remove("is-loading");
			this.node
				.querySelectorAll("fieldset")
				.forEach((facet) => facet.removeAttribute("disabled"));

			// Only scroll if the results are not visible in viewport
			if (!append && this.results) {
				const resultsRect = this.results.getBoundingClientRect();
				const isResultsVisible =
					resultsRect.top >= 0 &&
					resultsRect.top <= window.innerHeight;

				if (!isResultsVisible) {
					this.results.scrollIntoView({
						behavior: "smooth",
						block: "start",
					});
				}
			}
		}
	}

	getFacetMode() {
		if (
			this.facets &&
			this.facetsFacets.length > 0 &&
			this.facetsFacets[0].offsetWidth < this.facets.offsetWidth
		) {
			this.facetsMode = "dropdown";
		} else {
			this.facetsMode = "collapse";
		}

		return this.facetsMode;
	}

	toggleFacets() {
		if (this.facets) {
			return this.facets.classList.contains(this.opts.facetsOpened)
				? this.closeFacets()
				: this.openFacets();
		}

		return false;
	}

	openFacets() {
		if (this.facets) {
			return this.facets.classList.add(this.opts.facetsOpened);
		}

		return false;
	}

	closeFacets() {
		if (this.facets) {
			this.facets.classList.remove(this.opts.facetsOpened);
		}

		return false;
	}
}

export let instances = [];

document.addEventListener("DOMContentLoaded", () => {
	instances = Array.from(document.querySelectorAll(".advanced-query")).map(
		(node) => new AdvancedQuery(node)
	);
});
